const SIRV_SCRIPT_URL = 'https://scripts.sirv.com/sirv.js';
const SIRV_PROMISE_KEY = '__productSpinSirvPromise';
const SIRV_LOAD_TIMEOUT = 15000;

function findSirvScript() {
  for (const script of document.scripts) {
    if (script.src === SIRV_SCRIPT_URL) return script;
  }

  return null;
}

function loadSirv() {
  if (typeof window.Sirv?.start === 'function') {
    return Promise.resolve(window.Sirv);
  }

  if (window[SIRV_PROMISE_KEY]) {
    return window[SIRV_PROMISE_KEY];
  }

  const promise = new Promise((resolve, reject) => {
    const existingScript = findSirvScript();
    const script = existingScript || document.createElement('script');
    let timeoutId;

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };

    const handleLoad = () => {
      cleanup();

      if (typeof window.Sirv?.start !== 'function') {
        if (script.dataset.productSpinSirv === 'true') script.remove();
        reject(new Error('Sirv loaded without its start API.'));
        return;
      }

      resolve(window.Sirv);
    };

    const handleError = () => {
      cleanup();
      if (script.dataset.productSpinSirv === 'true') script.remove();
      reject(new Error('Sirv failed to load.'));
    };

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });

    if (!existingScript) {
      script.src = SIRV_SCRIPT_URL;
      script.async = true;
      script.dataset.productSpinSirv = 'true';
      document.head.appendChild(script);
    }

    if (typeof window.Sirv?.start === 'function') {
      handleLoad();
    } else {
      timeoutId = window.setTimeout(handleError, SIRV_LOAD_TIMEOUT);
    }
  });

  window[SIRV_PROMISE_KEY] = promise;
  void promise.catch(() => {
    if (window[SIRV_PROMISE_KEY] === promise) {
      delete window[SIRV_PROMISE_KEY];
    }
  });

  return promise;
}

class ProductSpin extends HTMLElement {
  #controller;
  #trigger;
  #overlay;
  #mount;
  #loading;
  #error;
  #closeButton;
  #expandButton;
  #expandLabel;
  #reduceLabel;
  #productImages;
  #spinId;
  #spinUrl;
  #initialized = false;
  #initializing = false;
  #isOpen = false;
  #isExpanded = false;
  #backgroundElements = [];

  connectedCallback() {
    this.#controller = new AbortController();
    this.#trigger = this.querySelector('[data-product-spin-trigger]');
    this.#overlay = this.querySelector('[data-product-spin-overlay]');
    this.#mount = this.querySelector('[data-product-spin-mount]');
    this.#loading = this.querySelector('[data-product-spin-loading]');
    this.#error = this.querySelector('[data-product-spin-error]');
    this.#closeButton = this.querySelector('[data-product-spin-close]');
    this.#expandButton = this.querySelector('[data-product-spin-expand]');
    this.#expandLabel = this.querySelector('[data-product-spin-expand-label]');
    this.#reduceLabel = this.querySelector('[data-product-spin-reduce-label]');
    this.#productImages = this.closest('product-images');
    this.#spinId = this.dataset.spinId;
    this.#spinUrl = this.#validateSpinUrl(this.dataset.spinUrl);

    if (
      !this.#trigger ||
      !(this.#overlay instanceof HTMLDialogElement) ||
      !this.#mount ||
      !this.#closeButton ||
      !this.#expandButton
    ) {
      return;
    }

    this.#trigger.addEventListener('click', () => this.#open(), { signal: this.#controller.signal });
    this.#closeButton.addEventListener('click', () => this.#close(), { signal: this.#controller.signal });
    this.#expandButton.addEventListener('click', () => void this.#toggleExpanded(), {
      signal: this.#controller.signal
    });
    this.#overlay.addEventListener('cancel', (event) => this.#handleDialogCancel(event), {
      signal: this.#controller.signal
    });
    this.querySelector('[data-product-spin-retry]')?.addEventListener('click', () => this.#start(), {
      signal: this.#controller.signal
    });
    document.addEventListener('keydown', (event) => this.#handleKeydown(event), {
      signal: this.#controller.signal
    });
    this.#productImages?.addEventListener('click', (event) => this.#handleThumbnailActivation(event), {
      capture: true,
      signal: this.#controller.signal
    });
    this.#productImages?.addEventListener('keydown', (event) => this.#handleThumbnailKeydown(event), {
      capture: true,
      signal: this.#controller.signal
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    if (this.#overlay?.open) this.#overlay.close();
    this.#setScrollLock(false);
    this.#restoreBackground();

    if (this.#initialized && typeof window.Sirv?.stop === 'function') {
      try {
        window.Sirv.stop(this.#spinId);
      } catch (error) {
        // The viewer may already have been removed by a section reload.
      }
    }
  }

  #validateSpinUrl(value) {
    try {
      const url = new URL(value);
      const isSpinPath = url.pathname.toLowerCase().endsWith('.spin');
      return url.protocol === 'https:' && isSpinPath ? url.href : null;
    } catch (error) {
      return null;
    }
  }

  async #open() {
    if (this.#isOpen) return;

    this.#isOpen = true;
    this.dataset.state = 'open';
    this.#trigger.setAttribute('aria-expanded', 'true');
    this.#setExpandedState(false);
    this.#overlay.show();
    this.#disableBackground();
    this.#closeButton.focus({ preventScroll: true });

    if (this.#initialized) {
      await this.#nextFrame();
      window.Sirv?.refresh?.();
      return;
    }

    await this.#start();
  }

  #close(returnFocus = true) {
    if (!this.#isOpen) return;

    this.#pauseSpin();
    this.#isOpen = false;
    this.dataset.state = 'closed';
    this.#trigger.setAttribute('aria-expanded', 'false');
    this.#setExpandedState(false);
    if (this.#overlay.open) this.#overlay.close();
    this.#restoreBackground();
    if (returnFocus) this.#trigger.focus({ preventScroll: true });
  }

  async #start() {
    if (this.#initialized || this.#initializing) return;

    if (!this.#spinUrl) {
      this.#showError();
      return;
    }

    this.#initializing = true;
    this.#setLoading(true);

    try {
      const sirv = await loadSirv();
      if (!this.isConnected || !this.#isOpen) return;

      const viewer = document.createElement('div');
      const viewerUrl = new URL(this.#spinUrl);
      viewerUrl.searchParams.set('zoom', '0');
      viewerUrl.searchParams.set('fullscreen', 'false');
      viewer.id = this.#spinId;
      viewer.className = 'Sirv product-spin__viewer';
      viewer.dataset.src = viewerUrl.href;
      viewer.dataset.mobileOptions = 'spinOnAnyDrag:false;';
      this.#mount.replaceChildren(viewer);

      await this.#nextFrame();
      sirv.start(this.#spinId);
      this.#initialized = true;
    } catch (error) {
      this.#mount.replaceChildren();
      this.#showError();
      console.error('Product 360° viewer failed to initialize.');
    } finally {
      this.#initializing = false;
      this.#setLoading(false);
    }
  }

  #setLoading(isLoading) {
    this.#overlay.setAttribute('aria-busy', String(isLoading));
    if (this.#loading) this.#loading.hidden = !isLoading;
    if (isLoading && this.#error) this.#error.hidden = true;
  }

  #showError() {
    this.#overlay.setAttribute('aria-busy', 'false');
    if (this.#loading) this.#loading.hidden = true;
    if (this.#error) this.#error.hidden = false;
  }

  #handleKeydown(event) {
    if (event.key !== 'Escape' || !this.#isOpen) return;
    event.preventDefault();

    if (this.#isExpanded) {
      void this.#setExpanded(false);
      return;
    }

    this.#close();
  }

  #handleDialogCancel(event) {
    if (!this.#isOpen || !this.#isExpanded) return;

    event.preventDefault();
    void this.#setExpanded(false);
  }

  #toggleExpanded() {
    return this.#setExpanded(!this.#isExpanded);
  }

  async #setExpanded(isExpanded) {
    if (!this.#isOpen || this.#isExpanded === isExpanded) return;

    if (this.#overlay.open) this.#overlay.close();
    this.#setExpandedState(isExpanded);

    if (isExpanded) {
      this.#overlay.showModal();
    } else {
      this.#overlay.show();
    }

    await this.#nextFrame();
    window.Sirv?.refresh?.();
    this.#expandButton.focus({ preventScroll: true });
  }

  #setExpandedState(isExpanded) {
    this.#isExpanded = isExpanded;
    this.dataset.view = isExpanded ? 'expanded' : 'embedded';
    this.#expandButton.setAttribute('aria-expanded', String(isExpanded));

    if (isExpanded) {
      this.#overlay.setAttribute('aria-modal', 'true');
    } else {
      this.#overlay.removeAttribute('aria-modal');
    }

    this.#setScrollLock(isExpanded);

    const label = isExpanded ? this.#expandButton.dataset.reduceLabel : this.#expandButton.dataset.expandLabel;
    if (label) this.#expandButton.setAttribute('aria-label', label);
    if (this.#expandLabel) this.#expandLabel.hidden = isExpanded;
    if (this.#reduceLabel) this.#reduceLabel.hidden = !isExpanded;
  }

  #setScrollLock(isLocked) {
    document.documentElement.classList.toggle('product-spin-expanded', isLocked);
    document.body?.classList.toggle('product-spin-expanded', isLocked);
  }

  #handleThumbnailActivation(event) {
    if (!this.#isOpen || !(event.target instanceof Element)) return;
    if (!event.target.closest('[data-product-thumb]')) return;

    this.#close(false);
  }

  #handleThumbnailKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    this.#handleThumbnailActivation(event);
  }

  #disableBackground() {
    const gallery = this.closest('.product__main-photos');
    if (!gallery) return;

    const backgroundElements = gallery.querySelectorAll('[data-product-photos], [data-shopify-xr]');
    for (const element of backgroundElements) {
      this.#backgroundElements.push({
        element,
        ariaHidden: element.getAttribute('aria-hidden'),
        hadInert: element.hasAttribute('inert')
      });
      element.setAttribute('aria-hidden', 'true');
      element.setAttribute('inert', '');
    }
  }

  #restoreBackground() {
    for (const state of this.#backgroundElements) {
      if (state.ariaHidden === null) {
        state.element.removeAttribute('aria-hidden');
      } else {
        state.element.setAttribute('aria-hidden', state.ariaHidden);
      }

      if (!state.hadInert) state.element.removeAttribute('inert');
    }

    this.#backgroundElements = [];
  }

  #pauseSpin() {
    if (!this.#initialized || typeof window.Sirv?.instance !== 'function') return;

    try {
      window.Sirv.instance(this.#spinId)?.pause?.();
    } catch (error) {
      // The normal gallery is already restored even if the Sirv instance has been removed.
    }
  }

  #nextFrame() {
    return new Promise((resolve) => window.requestAnimationFrame(resolve));
  }
}

if (!customElements.get('product-spin')) {
  customElements.define('product-spin', ProductSpin);
}
